import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { type ChainId, ChainUtils } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, ONE, parseApiAsset } from '@morpho-org/dapp-core';
import { useMemo } from 'react';
import { formatUnits, isAddressEqual, parseUnits } from 'viem';
import type { Warning } from '@/components/common/WarningIcon';
import { vaultV1PublicAllocatorAddresses } from '@/helpers/addresses';
import { getMarketUrl } from '@/helpers/routing';
import { getMarketWarningMessage } from '@/helpers/warningMessages';
import { apiWarningLevelToEnum } from '@/helpers/warnings';
import type { VaultReallocationTableRow } from '../config';
import type { VaultV1Reallocation } from '../reallocations.contract';
type AssetLabel = VaultV1Reallocation['market']['loanAsset'];
const toToken = (asset: AssetLabel, chainId: ChainId, priceUsd?: number) => parseApiAsset({
  address: asset.address,
  symbol: asset.symbol ?? undefined,
  decimals: asset.decimals,
  logoURI: asset.logoUri,
  priceUsd,
  chain: {
    id: chainId
  }
});

/** The loan token's USD price, recovered from the row's USD value so the amount cell picks its digits from it. */
function loanPriceUsd(item: VaultV1Reallocation): number | undefined {
  const units = Number(formatUnits(BigInt(item.assets), item.market.loanAsset.decimals));
  return item.assetsUsd === null || units === 0 ? undefined : item.assetsUsd / units;
}
type WarningLevel = Parameters<typeof apiWarningLevelToEnum>[0];
const isWarningLevel = (level: string): level is WarningLevel => level === 'red' || level === 'yellow' || level === 'green';
function toWarnings(warnings: VaultV1Reallocation['market']['warnings'], loanAsset: {
  symbol?: string;
  decimals: number;
}): Warning[] {
  return warnings.flatMap(warning => {
    if (!isWarningLevel(warning.level)) return [];
    const message = getMarketWarningMessage({
      type: warning.type,
      level: warning.level,
      metadata: {
        content: warning.content,
        badDebtAssets: warning.badDebtAssets,
        badDebtUsd: warning.badDebtUsd
      }
    }, loanAsset);
    return message ? [{
      level: apiWarningLevelToEnum(warning.level),
      message,
      warningType: warning.type
    }] : [];
  });
}
export interface ReallocateRows {
  items: readonly VaultV1Reallocation[];
  chainId: ChainId;
}

/** @internal */
export const useTableReallocateRow = t0 => {
  const $ = _c(3);
  const {
    items,
    chainId
  } = t0;
  let t1;
  if ($[0] !== chainId || $[1] !== items) {
    const identifier = InterfaceChainUtils.getIdentifierById(chainId);
    const publicAllocator = vaultV1PublicAllocatorAddresses[chainId];
    t1 = items.map(item => {
      const loanAsset = toToken(item.market.loanAsset, chainId, loanPriceUsd(item));
      const collateralAsset = item.market.collateralAsset ? toToken(item.market.collateralAsset, chainId) : null;
      const sign = item.type === "withdraw" ? -ONE : ONE;
      const marketId = item.market.marketId as MarketId;
      const marketUrl = collateralAsset ? getMarketUrl({
        identifier,
        marketId,
        collateralSymbol: collateralAsset.symbol ?? "",
        loanSymbol: loanAsset.symbol ?? ""
      }) : undefined;
      return {
        txHash: item.txHash,
        timestamp: item.timestamp,
        caller: item.caller,
        assets: BigInt(item.assets) * sign,
        amountValue: item.assetsUsd === null ? null : item.assetsUsd * Number(sign),
        token: loanAsset,
        urlCaller: ChainUtils.getExplorerAddressUrl(chainId, item.caller),
        urlHash: ChainUtils.getExplorerTransactionUrl(chainId, item.txHash),
        marketUrl,
        isPublicAllocator: publicAllocator ? isAddressEqual(item.caller, publicAllocator) : false,
        market: {
          marketId,
          loanAsset,
          collateralAsset,
          lltv: parseUnits(item.market.lltv.toString(), 18),
          warnings: toWarnings(item.market.warnings, loanAsset)
        }
      };
    });
    $[0] = chainId;
    $[1] = items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export default useTableReallocateRow;